import React, { useState } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Divider,
  Checkbox,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  SendOutlined,
  EyeOutlined,
} from '@ant-design/icons';
// Api
import { GET_SPORTS } from '../../api/sports/queries';
import { CREATE_CUSTOM_INTERVIEW_NOW_NOTIFICATION } from '../../api/interview-now-notification/mutations';
import { PREVIEW_CUSTOM_NOTIFICATION_ATHLETE_COUNT } from '../../api/interview-now-notification/queries';
// Types
import { InterviewQuestionTopic } from 'api/graphql-global-types';
// Helpers
import { sanitizeLocations } from 'helpers/place';
// Ui
import { errorNotification, successNotification } from '../../ui/Notification';
// Components
import UserSelect from './UserSelect';
import LocationSelect from 'components/common/LocationSelect/LocationSelect';
import DispatchSettingsCard from './DispatchSettingsCard';

const { Title, Text } = Typography;
const { Option } = Select;

const TOPIC_GROUP_OPTIONS = [
  { value: InterviewQuestionTopic.Trending, label: 'Trending' },
  { value: InterviewQuestionTopic.LocalSportScene, label: 'Local Sport Scene' },
  { value: InterviewQuestionTopic.Viral, label: 'Viral' },
];

const InterviewNowNotifications: React.FC = () => {
  const [form] = Form.useForm();
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const { data: sportsData } = useQuery(GET_SPORTS, {
    variables: { input: {} },
  });

  const [previewAthleteCount, { loading: previewLoading }] = useLazyQuery(
    PREVIEW_CUSTOM_NOTIFICATION_ATHLETE_COUNT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setPreviewCount(data.previewCustomNotificationAthleteCount);
      },
      onError: (error) => {
        errorNotification(error.message);
      },
    }
  );

  const [createNotification, { loading: sendLoading }] = useMutation(
    CREATE_CUSTOM_INTERVIEW_NOW_NOTIFICATION,
    {
      onCompleted: (data) => {
        const { questionCount, athleteCount } =
          data.createCustomInterviewNowNotification;
        successNotification(
          `Created ${questionCount} questions and dispatched notifications to ${athleteCount} athletes`
        );
        form.resetFields();
        setPreviewCount(null);
      },
      onError: (error) => {
        errorNotification(error.message);
      },
    }
  );

  const getFilterInput = () => {
    const values = form.getFieldsValue();
    const locations = sanitizeLocations(values.locations);
    // Backend accepts an array, but the BO currently only allows one sport.
    // We still serialize as `[sportName]` so the wire shape stays forward-
    // compatible if multi-select is enabled later without a backend change.
    return {
      sportNames: values.sportName ? [values.sportName] : [],
      ...(locations.length ? { locations } : {}),
      ...(values.targetUserIds?.length
        ? { targetUserIds: values.targetUserIds }
        : {}),
    };
  };

  const handlePreview = () => {
    const input = getFilterInput();
    if (!input.sportNames.length) {
      errorNotification('Please select at least one sport');
      return;
    }
    previewAthleteCount({ variables: { input } });
  };

  const handleSend = async () => {
    try {
      const values = await form.validateFields();
      const filledQuestions = (values.questions || []).filter(
        (q: { topic?: string; question?: string; topicGroup?: string }) =>
          q?.topic?.trim() && q?.question?.trim() && q?.topicGroup
      );
      const locations = sanitizeLocations(values.locations);
      const input = {
        // Wrap as `[sportName]` — see getFilterInput note about forward-compat.
        sportNames: [values.sportName],
        ...(locations.length ? { locations } : {}),
        ...(values.targetUserIds?.length
          ? { targetUserIds: values.targetUserIds }
          : {}),
        ...(filledQuestions.length ? { questions: filledQuestions } : {}),
        ...(values.overrideSnooze ? { overrideSnooze: true } : {}),
        ...(values.overrideUnsubscribe ? { overrideUnsubscribe: true } : {}),
      };
      await createNotification({ variables: { input } });
    } catch {
      // form validation errors shown by ant design
    }
  };

  const sports = sportsData?.sports || [];

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <Title level={3}>Interview Now - Custom Notifications</Title>
      <Text type="secondary">
        Notify athletes by sport or location, or target specific athletes
        directly. Optionally add custom questions to save; leave empty to just
        nudge them to record using existing topics.
      </Text>

      <div style={{ marginTop: 24 }}>
        <DispatchSettingsCard />
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ questions: [{}], locations: [] }}
        >
          <Title level={5}>Targeting</Title>

          <Form.Item
            name="sportName"
            label="Sport"
            rules={[{ required: true, message: 'Select a sport' }]}
          >
            <Select
              placeholder="Select sport"
              showSearch
              optionFilterProp="children"
            >
              {sports.map((sport: { id: string; name: string }) => (
                <Option key={sport.id} value={sport.name}>
                  {sport.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="locations"
            label="Locations (optional)"
            tooltip="Pick country / state / city via Places autocomplete. Each pick narrows by its specific country+state+city combination; multiple picks are OR-ed together."
          >
            <LocationSelect
              id="locations"
              placeholder="Search location..."
              value={form.getFieldValue('locations') || []}
              onChange={(locations) => form.setFieldsValue({ locations })}
            />
          </Form.Item>

          <Form.Item
            name="targetUserIds"
            label="Send to specific athletes (optional)"
            tooltip="When set, only these athletes are notified — sport and location filters are ignored for targeting. A sport is still required for the notification copy, and any custom questions are saved per selected athlete."
          >
            <UserSelect placeholder="Search athletes to target directly (by store name)" />
          </Form.Item>

          <Divider />
          <Title level={5}>Questions (optional)</Title>
          <Text type="secondary">
            Add custom questions to be saved alongside existing viral topics.
            Leave all rows empty to skip saving — the notification will still go
            out, prompting athletes to record using existing topics.
          </Text>

          <Form.List name="questions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: 'flex', marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item {...restField} name={[name, 'topic']}>
                      <Input placeholder="Topic name" style={{ width: 180 }} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'question']}>
                      <Input
                        placeholder="Question text"
                        style={{ width: 300 }}
                      />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'topicGroup']}>
                      <Select
                        placeholder="Group"
                        style={{ width: 160 }}
                        allowClear
                      >
                        {TOPIC_GROUP_OPTIONS.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    block
                  >
                    Add Question
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Divider />
          <Title level={5}>Admin Overrides</Title>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Use sparingly — bypasses what users have explicitly chosen."
          />

          <Form.Item name="overrideSnooze" valuePropName="checked">
            <Checkbox>
              <Text strong>Force push despite snooze</Text>{' '}
              <Text type="secondary">
                — send even if the athlete is in their cooldown period
              </Text>
            </Checkbox>
          </Form.Item>

          <Form.Item name="overrideUnsubscribe" valuePropName="checked">
            <Checkbox>
              <Text strong>Force push to unsubscribed athletes</Text>{' '}
              <Text type="secondary">
                — send even to athletes who explicitly disabled this
                notification
              </Text>
            </Checkbox>
          </Form.Item>

          <Divider />

          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={handlePreview}
              loading={previewLoading}
            >
              Preview Count
            </Button>
            {previewCount !== null && (
              <Text strong>{previewCount} athletes qualify</Text>
            )}
          </Space>

          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={sendLoading}
            >
              Send Notification
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default InterviewNowNotifications;
